import { ComponentRef, Injectable, Type, ViewContainerRef } from '@angular/core';
import { Log, IShortLogger } from '../services/logger.service';

/**
 * Service to handle dynamic component loading, lifecycle management and cleanup.
 * Provides a centralized way to create, manage, and destroy components dynamically.
 */
@Injectable({
  providedIn: 'root'
})
export class DynamicComponentService {
  private readonly logger: IShortLogger<DynamicComponentService> = Log.create(DynamicComponentService);

  constructor() {}

  /**
   * Load a component dynamically into a container
   *
   * @param container The ViewContainerRef where the component should be loaded
   * @param componentType The component class to instantiate
   * @param inputs Optional object with input values to set on the component
   * @returns ComponentRef to the created component
   */
  loadComponent<TComponent>(
    container: ViewContainerRef,
    componentType: Type<TComponent>,
    inputs?: { [key: string]: any }
  ): ComponentRef<TComponent> {
    try {
      // Clear the container first
      container.clear();

      // Create the component
      const componentRef = container.createComponent<TComponent>(componentType);

      // Set inputs if provided
      if (inputs) {
        Object.keys(inputs).forEach(key => {
          (componentRef.instance as any)[key] = inputs[key];
        });

        // Manually trigger change detection
        componentRef.changeDetectorRef.detectChanges();
      }

      return componentRef;
    } catch (error) {
      this.logger.e(`Error loading component ${componentType.name}:`, error);
      throw error;
    }
  }

  /**
   * Load a component and move it to a visible DOM element
   * Useful for components that need to be created via ViewContainerRef
   * but displayed elsewhere in the DOM.
   *
   * @param container The ViewContainerRef for component creation
   * @param targetSelector The CSS selector for the target container
   * @param componentType The component class to instantiate
   * @param inputs Optional object with input values to set on the component
   * @returns ComponentRef to the created component
   */
  loadComponentToTarget<TComponent>(
    container: ViewContainerRef,
    targetSelector: string,
    componentType: Type<TComponent>,
    inputs?: { [key: string]: any }
  ): ComponentRef<TComponent> | null {
    try {
      // Create the component in the container
      const componentRef = this.loadComponent<TComponent>(container, componentType, inputs);

      // Find the target container
      const targetElement = document.querySelector(targetSelector);
      if (!targetElement) {
        this.logger.e(`Target element not found: ${targetSelector}`);
        componentRef.destroy();
        return null;
      }

      // Clear the target container
      targetElement.innerHTML = '';

      // Move component to target
      const componentElement = componentRef.location.nativeElement;
      targetElement.appendChild(componentElement);

      return componentRef;
    } catch (error) {
      this.logger.e(`Error loading component to target:`, error);
      return null;
    }
  }

  /**
   * Destroy a component and clean up its resources
   *
   * @param componentRef Reference to the component to destroy
   */
  destroyComponent(componentRef: ComponentRef<any> | null): void {
    if (componentRef) {
      try {
        componentRef.destroy();
      } catch (error) {
        this.logger.e('Error destroying component:', error);
      }
    }
  }

  /**
   * Destroy multiple components at once
   *
   * @param componentRefs Array of component references to destroy
   */
  destroyComponents(componentRefs: (ComponentRef<any> | null)[]): void {
    componentRefs.forEach(ref => this.destroyComponent(ref));
  }

  clearContainers(...containers: ViewContainerRef[]): void {
    containers.forEach(container => container.clear());
  }
}
